/**
 * selectSlaForSku — typed redux read seam for the Delivery SLA UI.
 *
 * Strictly scoped to the requested SKU. The v2 response can be keyed by a code
 * other than the one requested, but the reducer settles both keys on SUCCESS, so
 * there is no need to scan the pincode for a populated entry — doing that would
 * hand back another size's / product's SLA (stale cards mid-fetch, and cards for
 * a product with no size selected).
 */
import { RawSlaOption } from './types';

interface SlaSliceEntry {
  options?: RawSlaOption[];
  isFetching?: boolean;
  error?: boolean;
}

const getEntry = (state: any, pincode?: string, skuId?: string): SlaSliceEntry | undefined => {
  if (!pincode || !skuId) return undefined;
  return state?.edd?.slaDetails?.[pincode]?.[skuId];
};

/** Raw SLA option array for the given pincode + SKU (empty when absent). */
export const getSlaOptionsForSku = (
  state: any,
  pincode?: string,
  skuId?: string
): RawSlaOption[] => getEntry(state, pincode, skuId)?.options || [];

export const getIsSlaFetching = (state: any, pincode?: string, skuId?: string): boolean => {
  const entry = getEntry(state, pincode, skuId);
  if (entry && typeof entry.isFetching === 'boolean') return entry.isFetching;
  return Boolean(state?.edd?.isSlaFetching);
};

export const getSlaError = (state: any, pincode?: string, skuId?: string): boolean =>
  Boolean(getEntry(state, pincode, skuId)?.error);

/**
 * Active scopedView option for the current size+pincode.
 * When set, offers / EMI should come from this option instead of aggregator variant.
 * Null when no pincode / scopedView not loaded → keep aggregator variant path.
 */
export const getSelectedSlaOption = (state: any): RawSlaOption | null =>
  state?.edd?.selectedSlaOption || null;
